import { http } from '@/api'

export interface Role {
  id: number
  name: string
  code: string
  description?: string
  level: number
  sort_order: number
  is_system: number // 0: 否, 1: 是
  status: number // 0: 禁用, 1: 启用
  created_at: string
  updated_at: string
  permissions_count?: number
  admins_count?: number
}

export interface RoleStatistics {
  total: number
  active: number
  inactive: number
  system_roles: number
  custom_roles: number
  level_distribution: Record<string, number>
}

export interface Permission {
  id: number
  name: string
  code: string
  module: string
  type: string
  description?: string
  parent_id?: number
  status: number // 0: 禁用, 1: 启用
  children?: Permission[]
}

export interface RoleListParams {
  page?: number
  per_page?: number
  search?: string
  status?: number // 0: 禁用, 1: 启用
  level?: number
  is_system?: boolean
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  all?: boolean // 获取所有角色，不分页
}

export interface CreateRoleData {
  name: string
  code: string
  description?: string
  level: number
  status?: number // 0: 禁用, 1: 启用
}

export interface UpdateRoleData extends Partial<CreateRoleData> {}

export interface AssignPermissionsData {
  role_id: number
  permission_ids: number[]
}

export interface BatchStatusData {
  ids: number[]
  status: boolean // true: 启用, false: 禁用
}

// 角色管理API
export const roleApi = {
  // 获取角色统计信息
  getStatistics(): Promise<ApiResponse<RoleStatistics>> {
    return http.get('roles/statistics')
  },

  // 获取角色列表
  getList(params?: RoleListParams): Promise<ApiResponse<PaginatedData<Role>>> {
    return http.get('roles', { params })
  },

  // 获取角色详情
  getDetail(id: number): Promise<ApiResponse<Role>> {
    return http.get(`roles/${id}`)
  },

  // 创建角色
  create(data: CreateRoleData): Promise<ApiResponse<Role>> {
    return http.post('roles', data)
  },

  // 更新角色
  update(id: number, data: UpdateRoleData): Promise<ApiResponse<Role>> {
    return http.put(`roles/${id}`, data)
  },

  // 删除角色
  delete(id: number): Promise<ApiResponse<null>> {
    return http.delete(`roles/${id}`)
  },

  // 获取角色权限
  getPermissions(id: number): Promise<ApiResponse<Permission[]>> {
    return http.get(`roles/${id}/permissions`)
  },

  // 分配权限给角色
  assignPermissions(data: AssignPermissionsData): Promise<ApiResponse<null>> {
    return http.post('roles/assign-permissions', data)
  },

  // 批量更新角色状态
  batchUpdateStatus(data: BatchStatusData): Promise<ApiResponse<null>> {
    return http.patch('roles/batch/status', data)
  },
}

// 权限管理API（角色相关部分）
export const permissionApi = {
  // 获取所有权限列表（用于角色权限分配）
  getList(params?: {
    module?: string
    type?: number // 1: 菜单, 2: 操作
    status?: number // 0: 禁用, 1: 启用
    tree?: boolean
    all?: boolean
  }): Promise<ApiResponse<Permission[]>> {
    return http.get('permissions', { params })
  },

  // 获取权限统计信息
  getStatistics(): Promise<ApiResponse<{
    total_permissions: number
    enabled_permissions: number
    menu_permissions: number
    operation_permissions: number
    modules: string[]
    permission_by_module: Record<string, number>
  }>> {
    return http.get('permissions/statistics')
  },

  // 获取权限选项（用于下拉选择）
  getOptions(params?: {
    type?: number
    module?: string
    tree?: boolean
  }): Promise<ApiResponse<Array<{
    value: number
    label: string
    code: string
    level: number
    type: number
    children?: any[]
  }>>> {
    return http.get('permissions/options', { params })
  },
}
